import React, { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { usePermissionsMode } from '../contexts/PermissionsModeContext';
import { usePermissions } from '../contexts/PermissionContext';
import { XMarkIcon, UserGroupIcon, UserIcon } from '@heroicons/react/24/outline';
import { Tooltip } from './ui/Tooltip';
import TypeAhead from './TypeAhead';
import { buildEntityOptions, resolveEntity } from './permissions/entityOptions';
import {
  registerFrame,
  setActiveComponentId,
  clearActiveComponentId,
  useFrameCount,
  useIsActiveComponent,
} from './permissions/frameRegistry';


interface PermissionConfigurableProps {
  componentId: string;
  componentName: string;
  children: React.ReactNode;
  className?: string;
  popoverDirection?: 'up' | 'down';
  /** If true, component is hidden by default until permissions are explicitly granted */
  defaultHidden?: boolean;
  /**
   * If false, the permissions frame (purple outline + handle) is suppressed even in
   * permissions mode. Use for repeated instances sharing the same componentId so only
   * one instance is decorated.
   */
  showHandle?: boolean;
  /** @deprecated Alias for `showHandle` — kept for existing call sites. */
  showCog?: boolean;
}

const PermissionConfigurable: React.FC<PermissionConfigurableProps> = ({
  componentId,
  componentName,
  children,
  className = '',
  popoverDirection = 'down',
  defaultHidden = false,
  showHandle,
  showCog = true,
}) => {
  const {
    permissionsMode,
    enabledGroups,
    enabledUsers,
    componentPermissions,
    savedPermissions,
    permissionsBaselineLoaded,
    updateComponentPermission,
    removeComponentPermission,
    setPageComponents,
    accessLevels,
  } = usePermissionsMode();

  const { permissions } = usePermissions();

  const [showPopover, setShowPopover] = useState(false);
  const [showCurrentPermissions, setShowCurrentPermissions] = useState(false);
  const [selectedEntityValue, setSelectedEntityValue] = useState<string>('');
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);

  // How many frames for this permission are on screen, and whether this permission is
  // the one being pointed at (possibly via a sibling frame elsewhere on the page).
  const regionCount = useFrameCount(componentId);
  const isActive = useIsActiveComponent(componentId);

  // Enabled groups, plus enabled users once the admin types (see buildEntityOptions)
  const entityOptions = useMemo(
    () => buildEntityOptions(enabledGroups, enabledUsers),
    [enabledGroups, enabledUsers],
  );

  // Reset selection when popover closes
  useEffect(() => {
    if (!showPopover) {
      setSelectedEntityValue('');
      setShowCurrentPermissions(false);
    }
  }, [showPopover]);

  // Register this component with pageComponents when in permissions mode
  useEffect(() => {
    if (permissionsMode) {
      setPageComponents(prev => {
        // Check if component is already registered
        if (prev.some(c => c.id === componentId)) {
          return prev;
        }
        return [...prev, { id: componentId, name: componentName }];
      });

      // Cleanup: remove this component when unmounting or when permissions mode turns off
      return () => {
        setPageComponents(prev => prev.filter(c => c.id !== componentId));
      };
    }
  }, [permissionsMode, componentId, componentName, setPageComponents]);

  // Calculate popover position when it opens. popoverDirection is the
  // *preferred* side; if it won't fit and the opposite side has more room
  // we flip (same pattern as TypeAhead). Top/left are also clamped to the
  // viewport so handles near screen edges still produce a fully-visible popover.
  useEffect(() => {
    if (showPopover && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const popoverHeight = 450; // Approximate
      const popoverWidth = 400;
      const margin = 8;

      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      let direction: 'up' | 'down' = popoverDirection;
      if (direction === 'down' && spaceBelow < popoverHeight + margin && spaceAbove > spaceBelow) {
        direction = 'up';
      } else if (direction === 'up' && spaceAbove < popoverHeight + margin && spaceBelow > spaceAbove) {
        direction = 'down';
      }

      let top = direction === 'up'
        ? rect.top - popoverHeight - margin
        : rect.bottom + margin;
      if (top < margin) top = margin;
      if (top + popoverHeight > window.innerHeight - margin) {
        top = Math.max(margin, window.innerHeight - popoverHeight - margin);
      }

      let left = rect.right - popoverWidth;
      if (left < margin) left = margin;
      if (left + popoverWidth > window.innerWidth - margin) {
        left = window.innerWidth - popoverWidth - margin;
      }

      setPopoverPosition({ top, left });
    }
  }, [showPopover, popoverDirection]);

  const selectedEntity = useMemo(
    () => (selectedEntityValue ? resolveEntity(selectedEntityValue, enabledGroups, enabledUsers) : null),
    [selectedEntityValue, enabledGroups, enabledUsers],
  );

  // Get current permissions for component
  const currentPermissions = componentPermissions.get(componentId);

  const savedAssignments = Object.entries(savedPermissions.get(componentId)?.permissions ?? {})
    .map(([id, grant]) => ({
      id,
      type: grant.entityType,
      label: resolveEntity(`${grant.entityType}:${id}`, enabledGroups, enabledUsers)?.label ?? id,
      accessLevel: grant.accessLevel,
      level: accessLevels.find((level) => level.id === grant.accessLevel)?.name ?? `Level ${grant.accessLevel}`,
      // Removed in this session (X) but not saved yet: the row stays listed with an Undo.
      pendingRemoval: !currentPermissions?.permissions[id],
    }))
    .sort((a, b) => a.type.localeCompare(b.type) || a.label.localeCompare(b.label));

  const removeAssignment = (entityId: string) => {
    removeComponentPermission(componentId, entityId);
    // The selector seeds a default grant for whatever entity is picked (effect below), so
    // removing the picked entity must also clear the pick or it would come straight back.
    if (selectedEntity?.id === entityId) setSelectedEntityValue('');
  };

  // When the user picks a group/user that has no existing row, register the default
  // access level immediately so the save flow includes it. Without this, the access
  // dropdown shows the default ("View") but the user can't save it as-is — the
  // <select> onChange only fires on a *different* value, so picking the default no-ops
  // and the save state never sees this entity. Picking the entity itself is a clear
  // intent to grant a permission, so seeding the default is safe.
  useEffect(() => {
    if (!selectedEntity) return;
    if (currentPermissions?.permissions[selectedEntity.id]) return;
    const defaultLevel = defaultHidden ? 0 : 1;
    updateComponentPermission(
      componentId,
      componentName,
      selectedEntity.id,
      selectedEntity.type,
      defaultLevel,
    );
    // updateComponentPermission isn't memoized in context but the early-return guard
    // prevents re-registration once the row exists, so loops aren't a concern.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEntity, currentPermissions]);

  // Check if user is in Developer group
  const isInDeveloperGroup = () => {
    const GLOBAL_ADMINS = ['john.duplechain@gwii.com'];
    if (permissions?.user_email && GLOBAL_ADMINS.includes(permissions.user_email.toLowerCase())) {
      return true;
    }

    const userGroupIds = permissions?.groups || [];
    return enabledGroups.some(group =>
      userGroupIds.includes(group.group_id) &&
      (group.group_name?.toLowerCase().includes('developer') ||
       group.display_name?.toLowerCase().includes('developer'))
    );
  };

  // The frame (purple outline + handle) replaces the old floating cog. It's drawn with
  // `outline` rather than `border` so wrapping a component never shifts its layout.
  const decorate = permissionsMode && (showHandle ?? showCog) && isInDeveloperGroup();

  // Count this frame as one region of its permission so every co-scoped frame can
  // report the total and highlight together.
  useEffect(() => {
    if (!decorate) return;
    return registerFrame(componentId);
  }, [decorate, componentId]);

  // An open popover keeps its own frame — and its siblings — highlighted.
  useEffect(() => {
    if (!decorate || !showPopover) return;
    setActiveComponentId(componentId);
    return () => clearActiveComponentId(componentId);
  }, [decorate, showPopover, componentId]);

  // If not in permissions mode, PermissionsContainer handles visibility/access level logic
  if (!permissionsMode) {
    return <>{children}</>;
  }

  // Check if any entity has permissions
  const hasAnyPermission = currentPermissions && Object.keys(currentPermissions.permissions).length > 0;

  // Check if there are unsaved changes
  const hasUnsavedChanges = () => {
    const saved = savedPermissions.get(componentId);
    const current = currentPermissions;

    if (!saved && current) return true;
    if (saved && !current) return true;
    if (!saved && !current) return false;

    const savedEntityIds = Object.keys(saved!.permissions);
    const currentEntityIds = Object.keys(current!.permissions);

    if (savedEntityIds.length !== currentEntityIds.length) return true;

    for (const entityId of currentEntityIds) {
      const savedPerms = saved!.permissions[entityId];
      const currentPerms = current!.permissions[entityId];

      if (!savedPerms && currentPerms) return true;
      if (savedPerms && !currentPerms) return true;
      if (!savedPerms && !currentPerms) continue;

      if (savedPerms.accessLevel !== currentPerms.accessLevel) {
        return true;
      }
    }

    return false;
  };

  // Get access level for selected entity
  const getEntityAccessLevel = (): number => {
    if (!selectedEntity) return defaultHidden ? 0 : 1;
    const perms = currentPermissions?.permissions[selectedEntity.id];
    if (!perms) return defaultHidden ? 0 : 1;
    return perms.accessLevel;
  };

  const handleAccessLevelChange = (newLevel: number) => {
    if (!selectedEntity) return;
    updateComponentPermission(componentId, componentName, selectedEntity.id, selectedEntity.type, newLevel);
  };

  const unsaved = hasUnsavedChanges();
  // Highlighted when this permission is being pointed at — from this frame's handle or
  // from any co-scoped frame's handle elsewhere on the page.
  const highlighted = isActive || showPopover;

  const frameClass = !decorate
    ? ''
    : [
        // Style is spelled out per-branch (`outline` = solid) so we never rely on
        // Tailwind's emitted order to resolve solid-vs-dashed.
        'outline-offset-2 rounded-sm transition-shadow',
        highlighted ? 'outline-2' : 'outline-1',
        unsaved
          ? 'outline outline-red-500'
          : hasAnyPermission
          ? 'outline outline-purple-500'
          : 'outline-dashed outline-purple-400/70',
        highlighted ? 'shadow-[0_0_0_4px_rgba(168,85,247,0.18)]' : '',
      ].join(' ');

  const handleClass = unsaved
    ? 'bg-red-600 hover:bg-red-500'
    : hasAnyPermission
    ? 'bg-purple-600 hover:bg-purple-500'
    : 'bg-purple-400 hover:bg-purple-500';

  const regionSuffix = regionCount > 1 ? ` (${regionCount} regions on this page)` : '';

  return (
    <div className={`relative ${frameClass} ${className}`}>
      {/* Permissions handle — a small grip riding on the frame's top edge.
          Only rendered for developers while permissions mode is on. */}
      {decorate && (
        <Tooltip content={`Configure permissions for ${componentName}${regionSuffix}`} placement="left">
          <button
            ref={buttonRef}
            type="button"
            aria-label={`Configure permissions for ${componentName}`}
            draggable={false}
            onDragStart={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              setShowPopover(!showPopover);
            }}
            onMouseEnter={() => setActiveComponentId(componentId)}
            onMouseLeave={() => {
              // Keep the highlight while this frame's popover is open.
              if (!showPopover) clearActiveComponentId(componentId);
            }}
            onFocus={() => setActiveComponentId(componentId)}
            onBlur={() => {
              if (!showPopover) clearActiveComponentId(componentId);
            }}
            onMouseDown={(e) => e.stopPropagation()}
            className={`absolute -top-[9px] right-1.5 z-[55] flex h-[12px] items-center gap-[2px] rounded-[3px] px-[3px] shadow-sm pointer-events-auto transition-transform hover:scale-125 ${handleClass} ${
              showPopover ? 'ring-2 ring-purple-300' : ''
            }`}
          >
            <span className="w-[2px] h-[2px] rounded-full bg-white/90" />
            <span className="w-[2px] h-[2px] rounded-full bg-white/90" />
            <span className="w-[2px] h-[2px] rounded-full bg-white/90" />
          </button>
        </Tooltip>
      )}

      {children}

      {/* Permission configuration popover - rendered via Portal */}
      {showPopover && createPortal(
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-[9998]"
            onClick={() => setShowPopover(false)}
          />

          {/* Popover */}
          <div
            className="fixed z-[9999] w-[400px] max-w-[calc(100vw-16px)] flex flex-col bg-panel rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden"
            style={{
              top: `${popoverPosition.top}px`,
              maxHeight: `calc(100dvh - ${popoverPosition.top + 8}px)`,
              left: `${popoverPosition.left}px`,
            }}
          >
            {/* Header */}
            <div className="shrink-0 bg-gradient-to-r from-purple-500 to-indigo-600 px-4 py-3">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <h4 className="text-sm font-semibold text-white">{componentName}</h4>
                  <p className="text-xs text-white/80 mt-0.5">
                    <span>Configure permissions</span>
                    {/* One permission often wraps several disconnected regions; say so
                        outright so the frame isn't read as the permission's full scope. */}
                    {regionCount > 1 && <span> · {regionCount} regions on this page</span>}
                  </p>
                </div>
                <button
                  onClick={() => setShowPopover(false)}
                  className="text-white/80 hover:text-white transition-colors"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="p-4 space-y-4 min-h-0 overflow-y-auto">
              <button
                type="button"
                aria-expanded={showCurrentPermissions}
                onClick={() => setShowCurrentPermissions((shown) => !shown)}
                className="inline-flex items-center gap-2 rounded-md border border-rule px-3 py-2 text-sm text-heading hover:bg-highlight"
              >
                <UserGroupIcon className="h-4 w-4" aria-hidden="true" />
                Current permissions
              </button>
              {showCurrentPermissions && (
                <section aria-label="Current permissions" className="rounded-md border border-rule p-3 text-sm text-heading">
                  <p className="mb-2 text-xs text-muted">Saved assignments only. Unsaved changes and inherited access are not listed.</p>
                  {!permissionsBaselineLoaded ? (
                    <p role="status">Permissions have not finished loading.</p>
                  ) : savedAssignments.length === 0 ? (
                    <p>No explicit permissions assigned.</p>
                  ) : (
                    <ul className="max-h-48 space-y-2 overflow-y-auto">
                      {savedAssignments.map((assignment) => (
                        <li key={`${assignment.type}:${assignment.id}`} className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="break-words">{assignment.label}</div>
                            <div className="text-xs text-muted">{assignment.type === 'group' ? 'Group' : 'User'}</div>
                          </div>
                          {assignment.pendingRemoval ? (
                            <span className="inline-flex shrink-0 items-center gap-2 text-xs">
                              <span className="text-muted">Removed — save to apply</span>
                              <button
                                type="button"
                                onClick={() => updateComponentPermission(componentId, componentName, assignment.id, assignment.type, assignment.accessLevel)}
                                className="font-medium text-accent-text hover:underline"
                              >
                                Undo
                              </button>
                            </span>
                          ) : (
                            <span className="inline-flex shrink-0 items-center gap-1">
                              <span className="rounded bg-highlight px-2 py-0.5 text-xs">{assignment.level}</span>
                              <button
                                type="button"
                                aria-label={`Remove permission for ${assignment.label}`}
                                title="Remove this permission"
                                onClick={() => removeAssignment(assignment.id)}
                                className="rounded p-0.5 text-muted hover:bg-highlight hover:text-heading"
                              >
                                <XMarkIcon className="h-4 w-4" aria-hidden="true" />
                              </button>
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              )}
              {/* Entity Selector with TypeAhead */}
              <div>
                <label className="block text-xs font-medium text-label mb-2">
                  Select Group or User
                </label>
                {entityOptions.length === 0 ? (
                  <div className="px-3 py-2 bg-gray-100 dark:bg-gray-700 rounded-md text-sm text-gray-600 dark:text-gray-400">
                    No groups or users enabled
                  </div>
                ) : (
                  <TypeAhead
                    options={entityOptions}
                    value={selectedEntityValue}
                    onChange={setSelectedEntityValue}
                    placeholder="Search groups or users..."
                    showSearch={true}
                    showIcons={true}
                    showDescriptions={true}
                    groupBy={true}
                    maxHeight="max-h-60"
                  />
                )}
                {enabledUsers.length > 0 && (
                  <p className="mt-1.5 text-xs text-muted">
                    Groups are listed — type a name or email to find a user.
                  </p>
                )}
              </div>

              {/* Access Level Selector */}
              {selectedEntity && (
                <div className="space-y-3 pt-2 border-t border-gray-200 dark:border-gray-700">
                  {/* Entity Type Badge */}
                  <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400 rounded">
                      {selectedEntity.type === 'user' ? (
                        <>
                          <UserIcon className="w-3.5 h-3.5" />
                          User
                        </>
                      ) : (
                        <>
                          <UserGroupIcon className="w-3.5 h-3.5" />
                          Group
                        </>
                      )}
                    </span>
                    <span className="font-medium">{selectedEntity.label}</span>
                  </div>

                  {/* Access Level Selector */}
                  <div>
                    <label className="block text-xs font-medium text-label mb-1.5">
                      Access Level
                    </label>
                    <select
                      value={getEntityAccessLevel()}
                      onChange={(e) => handleAccessLevelChange(parseInt(e.target.value, 10))}
                      className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-sm text-heading focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      {accessLevels.map((level) => (
                        <option key={level.id} value={level.id}>
                          {level.name} — {level.description}
                        </option>
                      ))}
                    </select>
                    <p className="mt-1.5 text-xs text-muted">
                      {getEntityAccessLevel() === 0 && 'Component will be completely hidden from this entity'}
                      {getEntityAccessLevel() === 1 && 'Component will be visible but all inputs will be disabled'}
                      {getEntityAccessLevel() === 2 && 'Component will be fully interactive'}
                      {getEntityAccessLevel() >= 3 && 'Component will be fully interactive, and exempt from any requirement it enforces. Only components that enforce one treat this differently from Edit.'}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="shrink-0 bg-gray-50 dark:bg-gray-900/50 px-4 py-2 border-t border-gray-200 dark:border-gray-700">
              <p className="text-xs text-muted">
                Component ID: <code className="text-xs bg-gray-200 dark:bg-gray-700 px-1.5 py-0.5 rounded">{componentId}</code>
              </p>
            </div>
          </div>
        </>,
        document.body
      )}
    </div>
  );
};

export default PermissionConfigurable;
